// =============================================================================
// Delegation of Approvals — Service Layer
// PRD §7 (mock strategy) & §9.2 (API contract)
//
// Toggle between mock and real API:
//   VITE_DELEGATION_MOCK=true   → uses local mock data (no backend needed)
//   VITE_DELEGATION_MOCK=false  → calls real REST endpoints
// =============================================================================

import { apiClient, ApiError } from '@/services/apiClient';
import type {
  ApprovalDelegation,
  DelegationSettings,
  AdminDelegationListResponse,
  DelegationAuditFilter,
  CreateDelegationRequest,
  UpdateDelegationRequest,
  RevokeDelegationRequest,
  RevokeDelegationResponse,
  DelegationStatusFilter,
  DelegationApiError,
  PendingTimesheetCountResponse,
  ReassignPendingResponse,
  EligibleDelegate,
} from '../types/delegation.types';

import {
  MOCK_MY_DELEGATIONS,
  MOCK_DELEGATED_TO_ME,
  MOCK_DELEGATION_SETTINGS,
  MOCK_ALL_DELEGATIONS_ADMIN,
  MOCK_ERROR_TRIGGERS,
  MOCK_ACTIVE_DELEGATION,
  MOCK_USERS,
  MOCK_PENDING_TIMESHEET_COUNT,
} from '../mocks/delegationMocks';

// Lookup table for resolving delegateId → display name in mock create
const MOCK_DELEGATE_NAMES: Record<string, string> = Object.fromEntries(
  Object.values(MOCK_USERS).map((u) => [u.id, u.name])
);

// ---------------------------------------------------------------------------
// Feature flag
// ---------------------------------------------------------------------------

// Default to mock unless explicitly set to 'false' — safe for demo / testing
const USE_MOCK = import.meta.env?.VITE_DELEGATION_MOCK !== 'false';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Simulated network latency for mock calls */
function delay(ms = 400): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Type guard — narrows an unknown catch value to DelegationApiError.
 */
export function isDelegationApiError(err: unknown): err is DelegationApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'error' in err &&
    'message' in err
  );
}

/**
 * Extract a DelegationApiError from an ApiError thrown by apiClient.
 * When the real API returns a structured error body (e.g. { error: "overlap_conflict", message: "..." }),
 * apiClient wraps it in an ApiError and stores the raw body in `data`.
 * This helper extracts and returns the DelegationApiError if present, or null otherwise.
 */
export function extractDelegationApiError(err: unknown): DelegationApiError | null {
  if (err instanceof ApiError && err.data && typeof err.data.error === 'string' && typeof err.data.message === 'string') {
    return {
      error: err.data.error as DelegationApiError['error'],
      message: err.data.message as string,
      conflictingDelegation: err.data.conflictingDelegation as DelegationApiError['conflictingDelegation'],
    };
  }
  return null;
}

/**
 * Filter delegations by status for client-side filtering in mock mode.
 * Mirrors query-param filtering that the real API handles server-side.
 */
export function filterByStatus(
  delegations: ApprovalDelegation[],
  filter: DelegationStatusFilter
): ApprovalDelegation[] {
  if (filter === 'all') return delegations;

  const today = new Date().toISOString().split('T')[0];

  switch (filter) {
    case 'active':
      return delegations.filter((d) => d.status === 'active');



    case 'upcoming':
      // Active but start date is in the future
      return delegations.filter(
        (d) => d.status === 'active' && d.startDate > today
      );

    case 'expired':
      return delegations.filter((d) => d.status === 'expired');

    case 'revoked':
      return delegations.filter((d) => d.status === 'revoked');

    default:
      return delegations;
  }
}

// ---------------------------------------------------------------------------
// In-memory mutable store (persists within a browser session)
// Allows mutating operations (create/accept/decline/revoke) to reflect in UI
// ---------------------------------------------------------------------------

let _mockMyDelegations = [...MOCK_MY_DELEGATIONS];
let _mockDelegatedToMe = [...MOCK_DELEGATED_TO_ME];
let _mockSettings = { ...MOCK_DELEGATION_SETTINGS };
let _mockIdCounter = 100;

function resetMockStore(): void {
  _mockMyDelegations = [...MOCK_MY_DELEGATIONS];
  _mockDelegatedToMe = [...MOCK_DELEGATED_TO_ME];
  _mockSettings = { ...MOCK_DELEGATION_SETTINGS };
}

// Expose reset for testing purposes only
export const _testHelpers = { resetMockStore };

// ---------------------------------------------------------------------------
// Mock API implementation
// ---------------------------------------------------------------------------

const mockApi = {
  async getMyDelegations(status?: DelegationStatusFilter): Promise<ApprovalDelegation[]> {
    await delay(350);
    const all = [..._mockMyDelegations];
    return status ? filterByStatus(all, status) : all;
  },

  async getDelegatedToMe(status?: DelegationStatusFilter): Promise<ApprovalDelegation[]> {
    await delay(350);
    const all = [..._mockDelegatedToMe];
    return status ? filterByStatus(all, status) : all;
  },

  async getDelegationById(id: string): Promise<ApprovalDelegation> {
    await delay(300);
    const found =
      _mockMyDelegations.find((d) => d.id === id) ||
      _mockDelegatedToMe.find((d) => d.id === id);
    if (!found) {
      throw { error: 'not_found', message: `Delegation ${id} not found` } satisfies DelegationApiError;
    }
    return { ...found };
  },

  async createDelegation(request: CreateDelegationRequest): Promise<ApprovalDelegation> {
    await delay(500);

    // --- Error trigger simulation (PRD §7.2) ---
    if (request.delegateId === MOCK_ERROR_TRIGGERS.SELF_DELEGATION) {
      throw {
        error: 'self_delegation',
        message: 'You cannot delegate to yourself.',
      } satisfies DelegationApiError;
    }

    if (request.delegateId === MOCK_ERROR_TRIGGERS.CIRCULAR_DELEGATION) {
      throw {
        error: 'circular_delegation',
        message: 'A circular delegation chain would be created. The selected person has already delegated their approvals to you.',
      } satisfies DelegationApiError;
    }

    if (request.delegateId === MOCK_ERROR_TRIGGERS.NOT_ELIGIBLE) {
      throw {
        error: 'delegate_not_eligible',
        message: 'The selected person does not have approval permissions and cannot be set as a delegate.',
      } satisfies DelegationApiError;
    }

    if (request.delegateId === MOCK_ERROR_TRIGGERS.OVERLAP_CONFLICT) {
      throw {
        error: 'overlap_conflict',
        message: 'An active delegation already exists for the selected date range.',
        conflictingDelegation: MOCK_ACTIVE_DELEGATION,
      } satisfies DelegationApiError;
    }

    // Validate date range
    if (request.endDate <= request.startDate) {
      throw {
        error: 'invalid_date_range',
        message: 'End date must be after start date.',
      } satisfies DelegationApiError;
    }

    // Validate max duration
    const start = new Date(request.startDate);
    const end = new Date(request.endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (days > _mockSettings.maxDelegationDays) {
      throw {
        error: 'exceeds_max_duration',
        message: `Delegation exceeds the maximum allowed duration of ${_mockSettings.maxDelegationDays} days.`,
      } satisfies DelegationApiError;
    }

    const newDelegation: ApprovalDelegation = {
      id: `del-${++_mockIdCounter}`,
      managerId: 'user-james-chen',
      managerName: 'James Chen',
      delegateId: request.delegateId,
      delegateName: MOCK_DELEGATE_NAMES[request.delegateId] ?? request.delegateId,
      startDate: request.startDate,
      endDate: request.endDate,
      status: 'active',
      scope: request.scope ?? 'all',
      notes: request.notes,
      createdAt: new Date().toISOString(),
      createdBy: 'user-james-chen',
      durationDays: days,

      auditEvents: [
        {
          id: `audit-${_mockIdCounter}-1`,
          delegationId: `del-${_mockIdCounter}`,
          event: 'created',
          actorId: 'user-james-chen',
          actorName: 'James Chen',
          occurredAt: new Date().toISOString(),
        },
      ],
    };

    _mockMyDelegations.unshift(newDelegation);
    return newDelegation;
  },

  async updateDelegation(id: string, request: UpdateDelegationRequest): Promise<ApprovalDelegation> {
    await delay(400);
    const idx = _mockMyDelegations.findIndex((d) => d.id === id);
    if (idx === -1) {
      throw { error: 'not_found', message: `Delegation ${id} not found` } satisfies DelegationApiError;
    }
    _mockMyDelegations[idx] = {
      ..._mockMyDelegations[idx],
      ...request,
      updatedAt: new Date().toISOString(),
    };
    return { ..._mockMyDelegations[idx] };
  },

  async revokeDelegation(id: string, request: RevokeDelegationRequest): Promise<RevokeDelegationResponse> {
    await delay(400);
    const idx = _mockMyDelegations.findIndex((d) => d.id === id);
    if (idx === -1) {
      throw { error: 'not_found', message: `Delegation ${id} not found` } satisfies DelegationApiError;
    }
    const current = _mockMyDelegations[idx];
    if (current.status !== 'active') {
      throw { error: 'already_actioned', message: 'Only active delegations can be revoked.' } satisfies DelegationApiError;
    }
    _mockMyDelegations[idx] = {
      ...current,
      status: 'revoked',
      revokedAt: new Date().toISOString(),
      revokedBy: 'user-james-chen',
      revokeReason: request.reason,
    };
    return {
      delegation: { ..._mockMyDelegations[idx] },
      message: 'Delegation revoked successfully.',
    };
  },

  async getDelegationSettings(): Promise<DelegationSettings> {
    await delay(300);
    return { ..._mockSettings };
  },

  async updateDelegationSettings(settings: Partial<DelegationSettings>): Promise<DelegationSettings> {
    await delay(450);
    _mockSettings = { ..._mockSettings, ...settings, updatedAt: new Date().toISOString() };
    return { ..._mockSettings };
  },

  async getAdminAuditLog(filter?: DelegationAuditFilter): Promise<AdminDelegationListResponse> {
    await delay(500);
    let items = MOCK_ALL_DELEGATIONS_ADMIN.map((delegation) => ({
      delegation,
      events: delegation.auditEvents ?? [],
    }));

    if (filter?.status && filter.status !== 'all') {
      items = items.filter((i) => i.delegation.status === filter.status);
    }
    if (filter?.managerId) {
      items = items.filter((i) => i.delegation.managerId === filter.managerId);
    }
    if (filter?.delegateId) {
      items = items.filter((i) => i.delegation.delegateId === filter.delegateId);
    }

    const page = filter?.page ?? 1;
    const pageSize = filter?.pageSize ?? 20;
    const start = (page - 1) * pageSize;

    return {
      items: items.slice(start, start + pageSize),
      totalCount: items.length,
      page,
      pageSize,
    };
  },

  // PRD §4 Feature B — pending timesheet count for reassign prompt
  async getPendingTimesheetCount(): Promise<PendingTimesheetCountResponse> {
    await delay(300);
    return { pendingCount: MOCK_PENDING_TIMESHEET_COUNT };
  },

  // PRD §4 Feature B — bulk reassign pending timesheets to delegate
  async reassignPendingTimesheets(delegationId: string): Promise<ReassignPendingResponse> {
    await delay(600);
    const delegation = _mockMyDelegations.find((d) => d.id === delegationId);
    if (!delegation) {
      throw { error: 'not_found', message: `Delegation ${delegationId} not found` } satisfies DelegationApiError;
    }
    // Use runtime count in case the constant is changed to 0 for testing empty-state path
    const pendingCount: number = MOCK_PENDING_TIMESHEET_COUNT;
    if (pendingCount === 0) {
      throw { error: 'not_found', message: 'No pending timesheets to reassign.' } satisfies DelegationApiError;
    }
    // Log a pending_reassigned audit event on the delegation record
    if (!delegation.auditEvents) delegation.auditEvents = [];
    delegation.auditEvents.push({
      id: `audit-${delegationId}-reassign`,
      delegationId,
      event: 'updated', // closest available event type; real API uses "pending_reassigned"
      actorId: MOCK_USERS.jamesChen.id,
      actorName: MOCK_USERS.jamesChen.name,
      notes: `${pendingCount} pending timesheets reassigned to ${delegation.delegateName}.`,
      occurredAt: new Date().toISOString(),
    });
    return { reassignedCount: pendingCount };
  },

  // Eligible delegates for the delegate picker (mock mode)
  async getEligibleDelegates(): Promise<EligibleDelegate[]> {
    await delay(300);
    return [
      MOCK_USERS.priyaSharma,
      MOCK_USERS.marcusWebb,
      MOCK_USERS.oliviaGrant,
      MOCK_USERS.sarahMitchell,
      MOCK_USERS.tomKipling,
    ];
  },
};

// ---------------------------------------------------------------------------
// Real API implementation
// ---------------------------------------------------------------------------

const realApi = {
  async getMyDelegations(status?: DelegationStatusFilter): Promise<ApprovalDelegation[]> {
    const params = status ? `?status=${status}` : '';
    return apiClient.get<ApprovalDelegation[]>(`/delegations/my-delegations${params}`);
  },

  async getDelegatedToMe(status?: DelegationStatusFilter): Promise<ApprovalDelegation[]> {
    const params = status ? `?status=${status}` : '';
    return apiClient.get<ApprovalDelegation[]>(`/delegations/delegated-to-me${params}`);
  },

  async getDelegationById(id: string): Promise<ApprovalDelegation> {
    return apiClient.get<ApprovalDelegation>(`/delegations/${id}`);
  },

  async createDelegation(request: CreateDelegationRequest): Promise<ApprovalDelegation> {
    return apiClient.post<ApprovalDelegation>('/delegations', request);
  },

  async updateDelegation(id: string, request: UpdateDelegationRequest): Promise<ApprovalDelegation> {
    return apiClient.put<ApprovalDelegation>(`/delegations/${id}`, request);
  },

  async revokeDelegation(id: string, request: RevokeDelegationRequest): Promise<RevokeDelegationResponse> {
    return apiClient.post<RevokeDelegationResponse>(`/delegations/${id}/revoke`, request);
  },

  async getDelegationSettings(): Promise<DelegationSettings> {
    return apiClient.get<DelegationSettings>('/delegations/settings');
  },

  async updateDelegationSettings(settings: Partial<DelegationSettings>): Promise<DelegationSettings> {
    return apiClient.put<DelegationSettings>('/delegations/settings', settings);
  },

  async getAdminAuditLog(filter?: DelegationAuditFilter): Promise<AdminDelegationListResponse> {
    const params = new URLSearchParams();
    if (filter?.status && filter.status !== 'all') params.set('status', filter.status);
    if (filter?.managerId) params.set('managerId', filter.managerId);
    if (filter?.delegateId) params.set('delegateId', filter.delegateId);
    if (filter?.fromDate) params.set('fromDate', filter.fromDate);
    if (filter?.toDate) params.set('toDate', filter.toDate);
    if (filter?.page) params.set('page', String(filter.page));
    if (filter?.pageSize) params.set('pageSize', String(filter.pageSize));
    const qs = params.toString();
    return apiClient.get<AdminDelegationListResponse>(`/delegations/audit${qs ? `?${qs}` : ''}`);
  },

  // PRD §4 Feature B — pending timesheet count (BE-22)
  async getPendingTimesheetCount(): Promise<PendingTimesheetCountResponse> {
    return apiClient.get<PendingTimesheetCountResponse>('/approvals/pending/count');
  },

  // PRD §4 Feature B — bulk reassign pending timesheets (BE-23)
  async reassignPendingTimesheets(delegationId: string): Promise<ReassignPendingResponse> {
    return apiClient.post<ReassignPendingResponse>(`/delegations/${delegationId}/reassign-pending`, {});
  },

  // Eligible delegates — fetch all employees from the API
  async getEligibleDelegates(): Promise<EligibleDelegate[]> {
    const employees = await apiClient.get<Array<{ id: number; name: string; email: string }>>('/employees');
    return employees.map((e) => ({ id: String(e.id), name: e.name, email: e.email }));
  },
};

// ---------------------------------------------------------------------------
// Public service export — single surface all components import
// ---------------------------------------------------------------------------

export const delegationService = USE_MOCK ? mockApi : realApi;

export type DelegationService = typeof delegationService;

// Screen-data-mode-aware factory (mirrors getUserRoleService pattern)
export const getDelegationService = (mode: 'demo' | 'live'): DelegationService => {
  return mode === 'live' ? realApi : mockApi;
};
